import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { LoaderCircle, ReceiptText, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useCapabilities } from "@/capabilities/CapabilityProvider";
import { money } from "@/components/ui";
import { useSession } from "@/session/SessionProvider";

import {
  BetSlipClearButton,
  BetSlipEmpty,
  BetSlipErrorNotice,
  BetSlipGateNotice,
  BetSlipModeTabs,
  BetSlipSelectionList,
  BetSlipSuccess,
  BetSlipSummary,
  SystemSizeField,
} from "./BetSlipPanel";
import { useBetSlip } from "./BetSlipProvider";
import {
  buildCouponRequest,
  couponQuoteOptions,
  couponReconciling,
  placeCouponRequest,
} from "./controller";
import {
  MIN_STAKE_CENTS,
  parseStakeCents,
  presentBetSlipCode,
  presentBetSlipError,
  stakeInputFromCents,
} from "./presentation";

const FOCUSABLE = "button, [href], select, input, textarea, [tabindex]";

type PlacementState = {
  pending: boolean;
};

export function BetSlipDrawer() {
  const betslip = useBetSlip();
  const clearBetslip = betslip.clear;
  const setBetslipOpen = betslip.setOpen;
  const { session, requestLogin } = useSession();
  const capabilities = useCapabilities();
  const queryClient = useQueryClient();
  const drawerRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const restoreFocusLabelRef = useRef<string | null>(null);
  const recentPointerTargetRef = useRef<{
    element: HTMLElement;
    at: number;
  } | null>(null);
  const restoreOnCloseRef = useRef(false);
  const wasOpenRef = useRef(false);
  const [placement, setPlacement] = useState<PlacementState | null>(null);
  const [stakeInput, setStakeInput] = useState(() =>
    stakeInputFromCents(betslip.stakeCents),
  );
  const [stakeInputValid, setStakeInputValid] = useState(true);

  const request = useMemo(
    () =>
      buildCouponRequest({
        couponType: betslip.couponType,
        systemK: betslip.systemK,
        stakeCents: betslip.stakeCents,
        legs: betslip.legs,
      }),
    [betslip.couponType, betslip.systemK, betslip.stakeCents, betslip.legs],
  );

  const quote = useQuery({
    ...couponQuoteOptions(request),
    enabled:
      betslip.open &&
      betslip.legs.length > 0 &&
      stakeInputValid &&
      betslip.stakeCents >= MIN_STAKE_CENTS,
  });

  const place = useMutation({
    mutationFn: () =>
      placeCouponRequest(queryClient, request, crypto.randomUUID()),
    onSuccess: (receipt) => {
      setPlacement({ pending: couponReconciling(receipt) });
      clearBetslip();
      setStakeInput("5.00");
      setStakeInputValid(true);
    },
  });

  const focusLauncher = useCallback(() => {
    let target = restoreFocusRef.current;
    if (!target || !document.contains(target)) {
      const label = restoreFocusLabelRef.current;
      if (label) {
        const matches = [
          ...document.querySelectorAll<HTMLElement>(
            "button[aria-label], [href][aria-label], [tabindex][aria-label]",
          ),
        ].filter((element) => element.getAttribute("aria-label") === label);
        target = matches.length === 1 ? matches[0] : null;
      }
    }
    target?.focus({ preventScroll: true });
  }, []);

  const closeDrawer = useCallback(
    (restoreFocus = true) => {
      setPlacement(null);
      restoreOnCloseRef.current = restoreFocus;
      if (restoreFocus) {
        focusLauncher();
        window.requestAnimationFrame(focusLauncher);
      }
      setBetslipOpen(false);
    },
    [focusLauncher, setBetslipOpen],
  );

  useLayoutEffect(() => {
    if (betslip.open) {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current && restoreOnCloseRef.current) {
      focusLauncher();
    }
    wasOpenRef.current = false;
    restoreOnCloseRef.current = false;
  }, [betslip.open, focusLauncher]);

  useEffect(() => {
    const rememberPointerTarget = (event: Event) => {
      if (!(event.target instanceof HTMLElement)) return;
      const element = event.target.closest<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (element) recentPointerTargetRef.current = { element, at: Date.now() };
    };
    document.addEventListener("pointerdown", rememberPointerTarget, true);
    document.addEventListener("click", rememberPointerTarget, true);
    return () => {
      document.removeEventListener("pointerdown", rememberPointerTarget, true);
      document.removeEventListener("click", rememberPointerTarget, true);
    };
  }, []);

  useEffect(() => {
    if (!betslip.open) return;
    const activeElement =
      document.activeElement instanceof HTMLElement &&
      document.activeElement !== document.body
        ? document.activeElement
        : null;
    const recentPointerTarget = recentPointerTargetRef.current;
    restoreFocusRef.current =
      (betslip.launcher && document.contains(betslip.launcher)
        ? betslip.launcher
        : null) ??
      activeElement ??
      (recentPointerTarget &&
      Date.now() - recentPointerTarget.at < 1_500 &&
      document.contains(recentPointerTarget.element)
        ? recentPointerTarget.element
        : null);
    restoreFocusLabelRef.current =
      restoreFocusRef.current?.getAttribute("aria-label") ?? null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() =>
      closeRef.current?.focus(),
    );

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key !== "Tab" || !drawerRef.current) return;
      const focusable = [
        ...drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ].filter(
        (element) =>
          !element.hasAttribute("disabled") &&
          element.getAttribute("tabindex") !== "-1",
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [betslip.launcher, betslip.open, closeDrawer]);

  const quoteReady = quote.isSuccess && !quote.isFetching;
  const quoteHasClosedLeg =
    quoteReady && quote.data?.legs.some((leg) => !leg.bettingOpen) === true;
  const canPlace =
    Boolean(session) &&
    !capabilities.loading &&
    capabilities.can("platform_betting") &&
    betslip.legs.length > 0 &&
    stakeInputValid &&
    betslip.stakeCents >= MIN_STAKE_CENTS &&
    quoteReady &&
    !quoteHasClosedLeg &&
    !place.isPending &&
    !place.isError;
  const error = place.error
    ? presentBetSlipError(place.error, "place")
    : quote.error
      ? presentBetSlipError(quote.error, "quote")
      : quoteHasClosedLeg
        ? presentBetSlipCode("RACE_LOCKED", "quote")
        : null;
  const currency = session?.currency ?? "USD";
  const submitLabel = place.isPending
    ? "Placing bet…"
    : error?.kind === "closed"
      ? "Betting closed"
      : quote.isFetching
        ? "Updating return…"
        : `Place ${money(betslip.stakeCents, currency)} bet`;

  function updateStake(value: string) {
    place.reset();
    setPlacement(null);
    setStakeInput(value);
    const cents = parseStakeCents(value);
    setStakeInputValid(cents !== null);
    betslip.setStakeCents(cents ?? 0);
  }

  function setQuickStake(cents: number) {
    place.reset();
    setPlacement(null);
    setStakeInput(stakeInputFromCents(cents));
    setStakeInputValid(true);
    betslip.setStakeCents(cents);
  }

  function clearSlip() {
    setPlacement(null);
    clearBetslip();
    setStakeInput("5.00");
    setStakeInputValid(true);
    place.reset();
  }

  function changeCouponType(
    couponType: "single" | "combined" | "system",
    systemK?: number,
  ) {
    place.reset();
    setPlacement(null);
    betslip.setCouponType(couponType, systemK);
  }

  function removeLeg(key: string) {
    place.reset();
    setPlacement(null);
    betslip.removeLeg(key);
  }

  function signInToBet() {
    closeDrawer(false);
    requestLogin();
  }

  return (
    <>
      <button
        aria-label="Close betslip"
        className={clsx(
          "drawer-scrim betslip-scrim",
          betslip.open && "drawer-scrim--visible",
        )}
        onClick={() => closeDrawer()}
        tabIndex={betslip.open ? 0 : -1}
        type="button"
      />
      <aside
        aria-hidden={!betslip.open}
        aria-labelledby="betslip-title"
        aria-modal={betslip.open || undefined}
        className={clsx(
          "betslip-drawer",
          betslip.open && "betslip-drawer--open",
        )}
        inert={!betslip.open ? true : undefined}
        id="betslip-drawer"
        ref={drawerRef}
        role="dialog"
      >
        <header className="betslip-drawer__header">
          <div className="betslip-drawer__title">
            <span aria-hidden>
              <ReceiptText />
            </span>
            <div>
              <strong id="betslip-title">Betslip</strong>
              <small>
                {betslip.legs.length}{" "}
                {betslip.legs.length === 1 ? "selection" : "selections"}
              </small>
            </div>
          </div>
          <button
            aria-label="Close betslip"
            className="betslip-drawer__close"
            onClick={() => closeDrawer()}
            ref={closeRef}
            type="button"
          >
            <X aria-hidden />
          </button>
        </header>

        <div className="betslip-drawer__content">
          {placement ? (
            <BetSlipSuccess
              onDone={() => closeDrawer()}
              pending={placement.pending}
            />
          ) : !betslip.legs.length ? (
            <BetSlipEmpty />
          ) : (
            <div
              aria-labelledby={`betslip-tab-${betslip.couponType}`}
              className="betslip-builder"
              id="betslip-builder-panel"
              role="tabpanel"
            >
              <BetSlipModeTabs
                couponType={betslip.couponType}
                legCount={betslip.legs.length}
                onChange={changeCouponType}
              />

              {betslip.couponType === "system" ? (
                <SystemSizeField
                  legCount={betslip.legs.length}
                  onChange={(size) => changeCouponType("system", size)}
                  systemK={betslip.systemK}
                />
              ) : null}

              <BetSlipSelectionList
                legs={betslip.legs}
                onRemove={removeLeg}
                quotedLegs={quote.data?.legs}
              />

              <BetSlipSummary
                currency={currency}
                inputValid={stakeInputValid}
                lines={quote.data?.lines}
                onInputChange={updateStake}
                onQuickStake={setQuickStake}
                potentialCents={
                  quoteReady ? quote.data?.potentialCents : undefined
                }
                quoteLoading={quote.isFetching}
                stakeCents={betslip.stakeCents}
                stakeInput={stakeInput}
              />

              <div className="betslip-action-panel">
                {error ? <BetSlipErrorNotice error={error} /> : null}
                {!error && session && capabilities.loading ? (
                  <BetSlipGateNotice
                    code="CAPABILITY_CHECK_PENDING"
                    message="Checking betting availability…"
                  />
                ) : null}
                {!error &&
                session &&
                !capabilities.loading &&
                !capabilities.can("platform_betting") ? (
                  <BetSlipGateNotice
                    code="CAPABILITY_BLOCKED_PLATFORM_BETTING"
                    message="Betting is not available in your region."
                  />
                ) : null}

                {session ? (
                  <button
                    className="betslip-submit"
                    disabled={!canPlace}
                    onClick={() => place.mutate()}
                    type="button"
                  >
                    {place.isPending || quote.isFetching ? (
                      <LoaderCircle aria-hidden />
                    ) : null}
                    {submitLabel}
                  </button>
                ) : (
                  <button
                    className="betslip-submit"
                    onClick={signInToBet}
                    type="button"
                  >
                    Sign in to bet
                  </button>
                )}

                <BetSlipClearButton onClear={clearSlip} />
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
